/** Status visuals can arrive before their entity; keep their original expiry while waiting. */
export interface StatusVFXEvent {
  targetId: string;
  effectId: string;
  active: boolean;
  abilityId?: string;
  duration?: number;
  [key: string]: any;
}

export const PENDING_STATUS_LIMIT = 256;
export const PENDING_STATUS_RETRY_MS = 100;
const keyOf = (targetId: string, effectId: string) => JSON.stringify([targetId, effectId]);
const expiry = (duration: number | undefined, now: number) =>
  typeof duration === 'number' && Number.isFinite(duration) ? now + Math.max(0, duration) : Infinity;

export class PendingStatusVFX {
  private readonly entries = new Map<string, { data: StatusVFXEvent; expiresAt: number }>();
  private nextRetryAt = 0;

  add(data: StatusVFXEvent, now = Date.now()): void {
    const key = keyOf(data.targetId, data.effectId);
    this.entries.delete(key);
    if (this.entries.size >= PENDING_STATUS_LIMIT) this.entries.delete(this.entries.keys().next().value!);
    this.entries.set(key, { data: { ...data }, expiresAt: expiry(data.duration, now) });
  }

  /** Refresh only a pending visual; already-rendered statuses retain their existing owner. */
  refresh(data: Omit<StatusVFXEvent, 'active'>, now = Date.now()): void {
    const entry = this.entries.get(keyOf(data.targetId, data.effectId));
    if (!entry) return;
    const abilityId = typeof data.data?.abilityId === 'string' ? data.data.abilityId : data.abilityId;
    entry.data = { ...entry.data, ...data, active: true, ...(abilityId ? { abilityId } : {}) };
    if (data.duration !== undefined) entry.expiresAt = expiry(data.duration, now);
  }

  remove(targetId: string, effectId: string): void {
    this.entries.delete(keyOf(targetId, effectId));
  }

  forget(targetId: string): void {
    for (const [key, entry] of this.entries) if (entry.data.targetId === targetId) this.entries.delete(key);
  }

  update(tryPresent: (data: StatusVFXEvent) => boolean, now = Date.now()): void {
    if (now < this.nextRetryAt || this.entries.size === 0) return;
    this.nextRetryAt = now + PENDING_STATUS_RETRY_MS;
    for (const [key, entry] of this.entries) {
      const remaining = entry.expiresAt - now;
      if (remaining <= 0) { this.entries.delete(key); continue; }
      // Owner-ended statuses have no duration; their explicit removal still owns teardown.
      const data = Number.isFinite(remaining) ? { ...entry.data, duration: remaining } : entry.data;
      if (tryPresent(data)) this.entries.delete(key);
    }
  }

  clear(): void {
    this.entries.clear();
    this.nextRetryAt = 0;
  }
}
